import type { CellContext } from '@tanstack/react-table';
import { memo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { isCellEditable } from '../../utils';

/**
 * OptimizedDefaultCell – same as DefaultCell but without useTableContext().
 * It accesses `table` via the CellContext that TanStack already provides,
 * eliminating a per-cell context subscription.
 */
const DefaultCellInner = <T,>({
  getValue,
  row: { index },
  column: {
    id,
    columnDef: { meta, header },
  },
  cell,
  table,
}: CellContext<T, unknown>) => {
  const initialValue = getValue() as string;
  const [value, setValue] = useState<string>(initialValue);

  const onBlur = () => {
    table.options.meta?.updateData(index, { id: value });
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  let placeholder = '';
  if (meta?.placeholder) {
    placeholder = meta.placeholder;
  } else if (typeof header === 'string') {
    placeholder = header;
  }

  if (!isCellEditable(meta?.editable, cell)) {
    return <span>{value}</span>;
  }

  return (
    <Input
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={onBlur}
      type="text"
      placeholder={placeholder}
    />
  );
};

DefaultCellInner.displayName = 'OptimizedDefaultCell';

export const OptimizedDefaultCell = memo(DefaultCellInner) as typeof DefaultCellInner;

const Input = styled.input`
  :focus-visible {
    outline: none;
  }
`;
